import { GoogleGenAI, LiveServerMessage, Modality, Blob } from '@google/genai';
import { FactProcess } from '../types';
import { ASYLUM_QUESTIONNAIRE } from '../data/asylumQuestionnaire';

/**
 * Service for managing Gemini Live API real-time voice sessions.
 */

function encode(bytes: Uint8Array) {
  let binary = '';
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function decode(base64: string) {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

async function decodeAudioData(
  data: Uint8Array,
  ctx: AudioContext,
  sampleRate: number,
  numChannels: number,
): Promise<AudioBuffer> {
  const dataInt16 = new Int16Array(data.buffer);
  const frameCount = dataInt16.length / numChannels;
  const buffer = ctx.createBuffer(numChannels, frameCount, sampleRate);

  for (let channel = 0; channel < numChannels; channel++) {
    const channelData = buffer.getChannelData(channel);
    for (let i = 0; i < frameCount; i++) {
      channelData[i] = dataInt16[i * numChannels + channel] / 32768.0;
    }
  }
  return buffer;
}

export class GeminiLiveService {
  private sessionPromise: Promise<any> | null = null;
  private audioContext: AudioContext | null = null;
  private inputAudioContext: AudioContext | null = null;
  private nextStartTime = 0;
  private sources = new Set<AudioBufferSourceNode>();
  private mediaStream: MediaStream | null = null;
  
  // Buffering for better transcript sync
  private currentInputText = "";
  private currentOutputText = "";

  async startLiveSession(
    intake: FactProcess,
    callbacks: {
      onTranscript: (text: string, role: 'AI' | 'Client') => void;
      onError: (err: any) => void;
      onTurnComplete?: () => void;
    }
  ) {
    const ai = new GoogleGenAI({ apiKey: import.meta.env.VITE_API_KEY });
    
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
      this.inputAudioContext = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 });
      this.mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      console.error('[Gemini Live] Initialization failed:', err);
      callbacks.onError(new Error('Microphone access or AudioContext initialization failed.'));
      return;
    }

    const questionnaireJSON = JSON.stringify(ASYLUM_QUESTIONNAIRE, null, 2);
    
    const systemInstruction = `
      You are Victoria, the Lead AI Intake Consultant at Lluislaw APC. 
      Your tone is professional, legally precise, and empathetic but authoritative.
      You are conducting a strategic intake for a ${intake.type} matter with client ${intake.client.name}.
      Respond in the client's preferred language (${intake.client.language}).
      
      CONVERSATION PROTOCOL:
      1. You must systematically cover the following asylum questionnaire sections:
      ${questionnaireJSON}
      2. Probe deeply for specific dates, locations, and perpetrator identities.
      3. Maintain an empathetic but objective stance.
      4. If the client mentions trauma, acknowledge it briefly and professionally.
      5. Your goal is to gather enough data to draft a complete legal declaration.
      
      STRICT RULE: Ask one clear question at a time. Do not overwhelm the client.
    KEEP ALL RESPONSES CONCISE: Under 40 words for smooth conversation flow.
    `;

    this.sessionPromise = ai.live.connect({
      model: 'gemini-2.5-flash-native-audio-preview-12-2025',
      callbacks: {
        onopen: () => {
          console.log('[Gemini Live] Session opened');
          if (this.inputAudioContext && this.mediaStream) {
            const source = this.inputAudioContext.createMediaStreamSource(this.mediaStream);
            const scriptProcessor = this.inputAudioContext.createScriptProcessor(4096, 1, 1);
            
            scriptProcessor.onaudioprocess = (audioProcessingEvent) => {
              const inputData = audioProcessingEvent.inputBuffer.getChannelData(0);
              const pcmBlob = this.createBlob(inputData);
              this.sessionPromise?.then((session) => {
                session.sendRealtimeInput({ media: pcmBlob });
              });
            };
            
            source.connect(scriptProcessor);
            scriptProcessor.connect(this.inputAudioContext.destination);
          }
        },
        onmessage: async (message: LiveServerMessage) => {
          const content = message.serverContent;
          if (!content) return;

          // Handle streaming transcriptions
          if (content.outputTranscription?.text) {
            this.currentOutputText += content.outputTranscription.text;
            callbacks.onTranscript(this.currentOutputText, 'AI');
          }
          
          if (content.inputTranscription?.text) {
            this.currentInputText += content.inputTranscription.text;
            callbacks.onTranscript(this.currentInputText, 'Client');
          }

          if (content.turnComplete) {
             this.currentInputText = "";
             this.currentOutputText = "";
             if (callbacks.onTurnComplete) callbacks.onTurnComplete();
          }

          const modelTurn = content.modelTurn;
          if (modelTurn?.parts && modelTurn.parts.length > 0) {
            const base64Audio = modelTurn.parts[0].inlineData?.data;
            
            if (base64Audio && this.audioContext) {
              this.nextStartTime = Math.max(this.nextStartTime, this.audioContext.currentTime);
              const audioBuffer = await decodeAudioData(
                decode(base64Audio),
                this.audioContext,
                24000,
                1
              );
              const source = this.audioContext.createBufferSource();
              source.buffer = audioBuffer;
              source.connect(this.audioContext.destination);
              source.start(this.nextStartTime);
              this.nextStartTime += audioBuffer.duration;
              this.sources.add(source);
              source.onended = () => this.sources.delete(source);
            }
          }

          if (content.interrupted) {
            this.sources.forEach(s => {
               try { s.stop(); } catch(e) {}
               this.sources.delete(s);
            });
            this.nextStartTime = 0;
            this.currentOutputText = "";
          }
        },
        onerror: (e: any) => {
          console.error('[Gemini Live] Connection error:', e);
          callbacks.onError(e);
        },
        onclose: (e: any) => console.log('[Gemini Live] Session closed', e),
      },
      config: {
        responseModalities: [Modality.AUDIO],
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Kore' } },
        },
        systemInstruction,
        outputAudioTranscription: {},
        inputAudioTranscription: {},
      }
    });

    return this.sessionPromise;
  }

  private createBlob(data: Float32Array): Blob {
    const l = data.length;
    const int16 = new Int16Array(l);
    for (let i = 0; i < l; i++) {
      int16[i] = data[i] * 32768;
    }
    return {
      data: encode(new Uint8Array(int16.buffer)),
      mimeType: 'audio/pcm;rate=16000',
    };
  }

  stopSession() {
    if (this.sessionPromise) {
      this.sessionPromise.then(session => {
        try { session.close(); } catch(e) {}
      });
      this.sessionPromise = null;
    }
    this.sources.forEach(s => {
      try { s.stop(); } catch(e) {}
      this.sources.delete(s);
    });
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach(track => track.stop());
      this.mediaStream = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    if (this.inputAudioContext) {
      this.inputAudioContext.close();
      this.inputAudioContext = null;
    }
  }
}

export const geminiLive = new GeminiLiveService();
